<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's'
      }
    ]"
  >
    <div
      v-if="!previewModeActive"
      :class="$style.top"
    >
      <Customer />
      <CustomerMember
        v-if="customerMember"
        :customerMember="customerMember"
      />
    </div>
    <div :class="$style.bottom">
      <div
        v-if="series.isSeriesMode"
        :class="$style.seriesSummary"
      >
        <div
          v-for="row in seriesSummaryRows"
          :key="row.test"
        >
          <BaseHeading
            size="s"
            :mb="0.25"
          >
            {{ row.label }}
          </BaseHeading>
          <BaseHeading
            size="l"
            v-test="row.test"
          >
            {{ row.value }}
          </BaseHeading>
        </div>
      </div>
      <div
        v-else
        :class="$style.summary"
      >
        <div :class="$style.date">
          <BaseHeading
            size="s"
            :mb="0.25"
          >
            {{ $t('global.date_time') }}
          </BaseHeading>
          <BaseHeading
            size="l"
            v-test="'appointment-summary-date'"
          >
            {{ filters.dateTimeLong(formData.startAt) }}
          </BaseHeading>
        </div>
        <div :class="$style.price">
          <BaseHeading
            size="s"
            :mb="0.25"
          >
            {{ $t('global.price') }}
          </BaseHeading>
          <BaseHeading
            size="l"
            v-test="'appointment-price'"
          >
            {{ filters.currency(totalPrice) }}
          </BaseHeading>
        </div>
      </div>
      <BaseCheckbox
        v-if="showNotificationsToggle"
        v-model="sendConfirmationEmail"
        :label="$t('appointment.create.email_notifications')"
        mt
        v-test="'appointment-notifications'"
      />
      <div :class="$style.buttonWrap">
        <BaseButton
          fullWidth
          size="l"
          :loading="isSaving"
          :disabled="
            isFetchingRequirements ||
            isFetchingAvailabilities ||
            !services.length
          "
          @click="$emit('submit')"
          v-test="'appointment-save'"
        >
          {{ submitLabel }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { storeToRefs } from 'pinia';
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import CustomerMember from '@/components/CustomerMember.vue';
import filters from '@/filters';
import { getPartResources } from '@/modules/calendar/appointment/helpers';
import { useCreateAppointmentStore } from '@/stores/calendar-create-appointment';
import { useCalendarPreviewStore } from '@/stores/calendar-preview';
import { useServicesStore } from '@/stores/services';
import Customer from './Customer.vue';

export default defineComponent({
  components: {
    Customer,
    CustomerMember,
  },
  props: {
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit'],
  setup() {
    const { t } = useI18n();
    const {
      formData,
      existingAppointmentData,
      customerData,
      customerMember,
      series,
    } = useCreateAppointmentStore();
    const {
      isFetchingRequirements,
      isFetchingAvailabilities,
      showNotificationsToggle,
      sendConfirmationEmail,
    } = storeToRefs(useCreateAppointmentStore());
    const { services } = storeToRefs(useServicesStore());
    const { servicePackageByServicePackageServiceId } = useServicesStore();

    const packageServicePrice = (servicePackageServiceId?: number | null) =>
      servicePackageByServicePackageServiceId(
        servicePackageServiceId,
      )?.servicePackageServices.find(
        (servicePackageService) =>
          servicePackageService.id === servicePackageServiceId,
      )?.price;

    const totalPrice = computed(() => {
      const { services } = useCreateAppointmentStore();
      let price = 0;
      formData.partsAttributes.forEach((part) => {
        const packagePrice = packageServicePrice(part.servicePackageServiceId);
        if (packagePrice !== undefined) {
          price += packagePrice;
          return;
        }

        const service = services.find(
          (service) => service.id === part.serviceId,
        );
        if (service) {
          let servicePrice = service.price;

          if (service.resourceAdjustments?.length) {
            // Check which employees are selected for this part
            const selectedEmployees = getPartResources(part, 'employee');

            // Check which adjustments to use based on the selected employees
            const activeAdjustments = service.resourceAdjustments.filter(
              (adj) => selectedEmployees.includes(adj.resourceId),
            );

            if (activeAdjustments.length) {
              // Get the highest price amongst all the price adjustments and use that as the actual price

              const prices = activeAdjustments.map((adj) => adj.price);

              if (selectedEmployees.length > activeAdjustments.length) {
                // In this case, there is at least one employee selected which does not have its own price
                // So we need to add the normal service price as an option, because the existing adjustments could be lower than the normal price
                // And we always need to show the highest price

                prices.push(service.price);
              }

              servicePrice = Math.max(...prices);
            }
          }

          price += servicePrice;
        }
      });
      return price;
    });

    const { previewModeActive } = storeToRefs(useCalendarPreviewStore());

    const submitLabel = computed(() => {
      if (existingAppointmentData.id) {
        return t('global.actions.save');
      }
      // For a repeat appointment the count is unknown, so keep the plain label.
      return series.isSeriesMode
        ? t('appointment.create.series.create_button', { count: series.count })
        : t('global.actions.create_appointment');
    });

    // The three rows shown in the series summary (first date, per-appointment
    // price, total price), so the template loops instead of repeating the block.
    const seriesSummaryRows = computed(() => [
      {
        label: t('appointment.create.series.first_date_time'),
        value: filters.dateTimeLong(formData.startAt),
        test: 'appointment-summary-date',
      },
      {
        label: t('appointment.create.series.price_per_appointment'),
        value: filters.currency(totalPrice.value),
        test: 'appointment-price',
      },
      {
        label: t('appointment.create.series.total_price'),
        value: filters.currency(totalPrice.value * series.count),
        test: 'appointment-total-price',
      },
    ]);

    return {
      formData,
      series,
      submitLabel,
      seriesSummaryRows,
      customerData,
      customerMember,
      totalPrice,
      isFetchingRequirements,
      isFetchingAvailabilities,
      existingAppointmentData,
      showNotificationsToggle,
      sendConfirmationEmail,
      filters,
      previewModeActive,
      services,
    };
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-bottom-right-radius: $radius;
}

.buttonWrap {
  margin-top: $spacing;
  padding-top: $spacing;
  border-top: 1px solid var(--color-border);
}

.top,
.bottom {
  .base:not(.smallScreen) & {
    padding: $spacing;
  }

  .base.smallScreen & {
    padding: $spacing $spacing * 0.5;
  }
}

.summary {
  display: flex;
}

.seriesSummary {
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.date,
.price {
  width: 50%;
}

.price {
  & > * {
    text-align: right;
    justify-content: flex-end;
  }
}
</style>
